// components/MusicPickerSheet.js
//
// Choosing background music for an invitation or a card: browse or search the
// Free To Use catalogue, hear a track, then choose which part of it loops and
// for how long. The web twin of the app's features/music/MusicPickerSheet.js;
// the catalogue, the saved shape and the window arithmetic are shared
// (shared/backgroundMusic.js), so a choice made here plays the same in the app.
//
//   browse  search, category chips (opened on the one that fits the event
//           type), the list; ▶ previews a track
//   trim    the waveform with the loop window over it - drag it along the
//           song, pick 15 / 30 / 60 / 90 s - playing the window as it moves
//
// Rendered through Portal, above everything, like every other sheet on the
// create forms.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiX, FiChevronLeft, FiSearch, FiPlay, FiPause, FiMusic } from 'react-icons/fi';
import Portal from './Portal';
import useLoopingAudio from '../hooks/useLoopingAudio';
import {
  fetchMusicCategories, searchMusic, fetchCategoryMusic, fetchFeaturedMusic, fetchMusicTrack,
} from '../services/musicCatalog';
import {
  categoryForEventType, clampWindow, loopLengthOptions, toBackgroundMusic,
  formatClock, waveformBars, DEFAULT_LOOP_LENGTH, MUSIC_ATTRIBUTION_URL,
} from '../shared/backgroundMusic';
import '../styles/MusicPickerSheet.scss';

const BARS = 64;

export default function MusicPickerSheet({ onClose, value, onChange, eventType }) {
  const [step, setStep] = useState('browse');
  const [categories, setCategories] = useState([]);
  const [categoryId, setCategoryId] = useState(null);
  const [query, setQuery] = useState('');
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [previewId, setPreviewId] = useState(null);
  const [track, setTrack] = useState(null);
  const [windowStart, setWindowStart] = useState(0);
  const [windowLength, setWindowLength] = useState(DEFAULT_LOOP_LENGTH);
  const [trimPlaying, setTrimPlaying] = useState(true);

  // Escape closes (or steps back); the page behind does not scroll.
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      if (step === 'trim') setStep('browse');
      else onClose();
    };
    document.addEventListener('keydown', onKey);
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previous;
    };
  }, [onClose, step]);

  // Categories, opened on the one that fits the invitation.
  useEffect(() => {
    let cancelled = false;
    fetchMusicCategories()
      .then((cats) => {
        if (cancelled) return;
        setCategories(cats);
        const want = categoryForEventType(eventType).toLowerCase();
        const match = cats.find((c) => c.name.toLowerCase() === want) || cats[0] || null;
        setCategoryId(match ? match.id : null);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [eventType]);

  // The list: search wins over the category.
  useEffect(() => {
    if (step !== 'browse') return undefined;
    const controller = new AbortController();
    const q = query.trim();
    const run = () => {
      setLoading(true);
      setError(null);
      const request = q.length >= 2
        ? searchMusic(q, { signal: controller.signal })
        : categoryId
          ? fetchCategoryMusic(categoryId, { signal: controller.signal })
          : fetchFeaturedMusic({ signal: controller.signal });
      request
        .then(setTracks)
        .catch((e) => {
          if (e?.name === 'AbortError') return;
          setError('Music could not be loaded. Check your connection and try again.');
          setTracks([]);
        })
        .finally(() => setLoading(false));
    };
    const t = setTimeout(run, q ? 350 : 0);
    return () => { clearTimeout(t); controller.abort(); };
  }, [step, query, categoryId]);

  // ── Audio ──────────────────────────────────────────────────────────────
  const listPreview = useMemo(() => {
    const t = tracks.find((x) => x.id === previewId);
    if (!t) return null;
    return { url: t.url, ...clampWindow({ duration: t.duration, start: t.duration * 0.2, length: 30 }) };
  }, [tracks, previewId]);

  const trimWindow = useMemo(
    () => (track ? { url: track.url, start: windowStart, length: windowLength } : null),
    [track, windowStart, windowLength],
  );

  const { ready } = useLoopingAudio(step === 'trim' ? trimWindow : listPreview, {
    playing: step === 'trim' ? trimPlaying : !!listPreview,
    fadeMs: 400,
    volume: 0.9,
  });

  // ── Actions ────────────────────────────────────────────────────────────
  const openTrim = (t, initial) => {
    const w = clampWindow({
      duration: t.duration,
      start: initial?.start ?? t.duration * 0.2,
      length: initial?.length ?? DEFAULT_LOOP_LENGTH,
    });
    setTrack(t);
    setWindowStart(w.start);
    setWindowLength(w.length);
    setPreviewId(null);
    setTrimPlaying(true);
    setStep('trim');
  };

  const editCurrent = () => {
    if (!value) return;
    openTrim(
      { id: value.trackId, title: value.title, artist: value.artist, artwork: value.artwork, url: value.url, duration: value.duration, waveform: [] },
      { start: value.start, length: value.length },
    );
    if (value.trackId) {
      fetchMusicTrack(value.trackId)
        .then((full) => {
          if (full) setTrack((t) => (t && t.id === full.id ? { ...t, waveform: full.waveform } : t));
        })
        .catch(() => {});
    }
  };

  const confirm = () => {
    if (!track) return;
    onChange(toBackgroundMusic(track, { start: windowStart, length: windowLength }));
    onClose();
  };

  const remove = () => {
    onChange(null);
    onClose();
  };

  return (
    <Portal>
      <div className="music-scrim" onClick={onClose}>
        <div
          className="music-sheet"
          role="dialog"
          aria-modal="true"
          aria-label="Music"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="music-head">
            {step === 'trim' ? (
              <button type="button" onClick={() => setStep('browse')} aria-label="Back to music">
                <FiChevronLeft />
              </button>
            ) : <span className="music-head-spacer" />}
            <h2>{step === 'trim' ? 'Choose the part' : 'Music'}</h2>
            <button type="button" onClick={onClose} aria-label="Close"><FiX /></button>
          </div>

          {step === 'browse' ? (
            <div className="music-body">
              <label className="music-search">
                <FiSearch />
                <input
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search music"
                  aria-label="Search music"
                  autoFocus
                />
              </label>

              {!query.trim() && categories.length > 0 && (
                <div className="music-chips" role="tablist" aria-label="Categories">
                  {categories.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      role="tab"
                      aria-selected={c.id === categoryId}
                      className={`music-chip ${c.id === categoryId ? 'is-active' : ''}`}
                      onClick={() => setCategoryId(c.id)}
                    >
                      {c.name}
                    </button>
                  ))}
                </div>
              )}

              {value && (
                <div className="music-current">
                  <FiMusic />
                  <button type="button" className="music-current-text" onClick={editCurrent}>
                    <strong>{value.title}</strong>
                    <span>{value.artist} · {formatClock(value.length)} loop · Adjust</span>
                  </button>
                  <button type="button" className="music-remove" onClick={remove}>Remove</button>
                </div>
              )}

              <div className="music-list">
                {tracks.map((t) => {
                  const isPreview = t.id === previewId;
                  return (
                    <div key={t.id} className="music-row">
                      <button type="button" className="music-row-main" onClick={() => openTrim(t)}>
                        <img src={t.artworkSmall || t.artwork} alt="" loading="lazy" />
                        <span className="music-row-text">
                          <strong>{t.title}</strong>
                          <span>{t.artist} · {formatClock(t.duration)}</span>
                        </span>
                      </button>
                      <button
                        type="button"
                        className="music-play"
                        onClick={() => setPreviewId(isPreview ? null : t.id)}
                        aria-label={isPreview ? `Stop ${t.title}` : `Preview ${t.title}`}
                      >
                        {isPreview && !ready ? <span className="music-spinner" /> : isPreview ? <FiPause /> : <FiPlay />}
                      </button>
                    </div>
                  );
                })}
                {!tracks.length && (
                  <p className="music-empty">
                    {loading ? 'Loading…' : (error || 'No music found. Try another word.')}
                  </p>
                )}
                {tracks.length > 0 && <p className="music-credit">Music from Free To Use · freetouse.com</p>}
              </div>
            </div>
          ) : (
            track && (
              <TrimStep
                track={track}
                start={windowStart}
                length={windowLength}
                onChangeStart={setWindowStart}
                onChangeLength={(l) => {
                  const w = clampWindow({ duration: track.duration, start: windowStart, length: l });
                  setWindowLength(w.length);
                  setWindowStart(w.start);
                }}
                playing={trimPlaying}
                onTogglePlay={() => setTrimPlaying((p) => !p)}
                loading={!ready}
                onConfirm={confirm}
              />
            )
          )}
        </div>
      </div>
    </Portal>
  );
}

function TrimStep({ track, start, length, onChangeStart, onChangeLength, playing, onTogglePlay, loading, onConfirm }) {
  const stripRef = useRef(null);
  const drag = useRef(null);
  const bars = useMemo(() => waveformBars(track.waveform, BARS), [track.waveform]);
  const duration = track.duration || 1;
  const options = loopLengthOptions(track.duration);

  // Drag anywhere on the strip; the grabbed point of the window stays under
  // the pointer rather than the window jumping to centre on it.
  const onPointerDown = (e) => {
    const el = stripRef.current;
    if (!el) return;
    el.setPointerCapture?.(e.pointerId);
    drag.current = { x: e.clientX, start, width: el.clientWidth };
  };
  const onPointerMove = (e) => {
    const d = drag.current;
    if (!d || !d.width) return;
    const next = clampWindow({ duration, start: d.start + ((e.clientX - d.x) / d.width) * duration, length });
    onChangeStart(next.start);
  };
  const onPointerUp = () => { drag.current = null; };

  // Arrow keys move the window a second at a time, for keyboard users.
  const onKeyDown = (e) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    e.preventDefault();
    const step = e.shiftKey ? 5 : 1;
    const next = clampWindow({ duration, start: start + (e.key === 'ArrowRight' ? step : -step), length });
    onChangeStart(next.start);
  };

  const left = (start / duration) * 100;
  const width = Math.max(4, (length / duration) * 100);

  return (
    <div className="music-body music-trim">
      {track.artwork && <img className="music-trim-art" src={track.artwork} alt="" />}
      <h3>{track.title}</h3>
      <p className="music-trim-artist">{track.artist}</p>

      <div
        ref={stripRef}
        className="music-wave"
        role="slider"
        tabIndex={0}
        aria-label="Part of the song that plays"
        aria-valuemin={0}
        aria-valuemax={Math.round(duration - length)}
        aria-valuenow={Math.round(start)}
        aria-valuetext={`${formatClock(start)} to ${formatClock(start + length)}`}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onKeyDown={onKeyDown}
      >
        {bars.map((h, i) => {
          const x = ((i + 0.5) / BARS) * 100;
          const inside = x >= left && x <= left + width;
          return <span key={i} className={`music-bar ${inside ? 'is-in' : ''}`} style={{ height: `${Math.round(h * 100)}%` }} />;
        })}
        <span className="music-window" style={{ left: `${left}%`, width: `${width}%` }} />
      </div>
      <div className="music-times">
        <span>{formatClock(start)}</span>
        <small>Drag to choose the part that plays</small>
        <span>{formatClock(start + length)}</span>
      </div>

      <p className="music-label">Loop length</p>
      <div className="music-lengths">
        {options.map((l) => (
          <button
            key={l}
            type="button"
            className={`music-chip ${Math.abs(l - length) < 0.5 ? 'is-active' : ''}`}
            onClick={() => onChangeLength(l)}
          >
            {formatClock(l)}
          </button>
        ))}
      </div>

      <div className="music-actions">
        <button type="button" className="music-round" onClick={onTogglePlay} aria-label={playing ? 'Pause' : 'Play'}>
          {loading && playing ? <span className="music-spinner" /> : playing ? <FiPause /> : <FiPlay />}
        </button>
        <button type="button" className="music-use" onClick={onConfirm}>Use this music</button>
      </div>

      <p className="music-credit">
        {track.title} by {track.artist} ·{' '}
        <a href={MUSIC_ATTRIBUTION_URL} target="_blank" rel="noopener noreferrer">Free To Use</a>
      </p>
    </div>
  );
}
